import { BaseService, ServiceResponse } from './base.service';
import type {
  ClubChannel,
  ClubChannelMessage,
  SendMessageRequest,
  ChannelFilters,
  MessageFilters,
  ChannelSubscriptionCallback,
  TypingSubscriptionCallback,
  TypingIndicator,
  UserProfile
} from '../../types/club-community';

/**
 * Optimized Club Community Service
 * Eliminates N+1 queries and fixes PostgREST relationship issues
 */
export class ClubCommunityOptimizedService extends BaseService {
  private static instance: ClubCommunityOptimizedService;
  private subscriptions: Map<string, any> = new Map();

  static getInstance(): ClubCommunityOptimizedService {
    if (!ClubCommunityOptimizedService.instance) {
      ClubCommunityOptimizedService.instance = new ClubCommunityOptimizedService();
    }
    return ClubCommunityOptimizedService.instance;
  }

  /**
   * Get club channels with their latest message and counts in a single optimized query
   */
  async getClubChannelsOptimized(clubId: string): Promise<ServiceResponse<ClubChannel[]>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Ensure default channels exist
      await this.ensureDefaultChannels(clubId);

      // Single query to get channels with basic info
      const { data: channels, error: channelsError } = await this.supabase
        .from('club_channels')
        .select(`
          id,
          name,
          description,
          channel_type,
          position,
          is_private,
          is_default,
          created_at
        `)
        .eq('club_id', clubId)
        .eq('is_private', false) // Only public channels for now
        .order('position')
        .order('created_at');

      if (channelsError) throw channelsError;
      if (!channels || channels.length === 0) return [];

      // Get all channel IDs for batch operations
      const channelIds = channels.map(c => c.id);

      // Batch query: Get latest message for each channel (without join to avoid 406 errors)
      const { data: latestMessages } = await this.supabase
        .from('club_channel_messages')
        .select(`
          id,
          channel_id,
          content,
          message_type,
          created_at,
          sender_id
        `)
        .in('channel_id', channelIds)
        .order('created_at', { ascending: false });

      // Batch query: Get message counts for each channel
      const messageCounts = await Promise.all(
        channelIds.map(async (channelId) => {
          const { count } = await this.supabase
            .from('club_channel_messages')
            .select('*', { count: 'exact', head: true })
            .eq('channel_id', channelId);
          return { channelId, count: count || 0 };
        })
      );

      // Batch query: Get last_read_at per channel for the current user
      const { data: memberReadData } = await this.supabase
        .from('club_channel_members')
        .select('channel_id, last_read_at')
        .eq('user_id', user.id)
        .in('channel_id', channelIds);

      const lastReadMap = new Map<string, string>(
        (memberReadData || []).map(m => [m.channel_id, m.last_read_at])
      );

      // Batch query: Get unread counts per channel
      const unreadCounts = await Promise.all(
        channelIds.map(async (channelId) => {
          const lastRead = lastReadMap.get(channelId);
          let query = this.supabase
            .from('club_channel_messages')
            .select('*', { count: 'exact', head: true })
            .eq('channel_id', channelId);
          if (lastRead) {
            query = query.gt('created_at', lastRead);
          }
          const { count } = await query;
          return { channelId, count: count || 0 };
        })
      );

      const unreadByChannel = new Map<string, number>(
        unreadCounts.map(({ channelId, count }) => [channelId, count])
      );

      // Process results: Group latest messages by channel
      const latestByChannel = new Map();
      const senderIds = new Set<string>();
      
      if (latestMessages) {
        for (const message of latestMessages) {
          if (!latestByChannel.has(message.channel_id)) {
            latestByChannel.set(message.channel_id, message);
            if (message.sender_id) {
              senderIds.add(message.sender_id);
            }
          }
        }
      }

      // Batch query: Get all sender profiles
      const senderProfiles = new Map();
      if (senderIds.size > 0) {
        const { data: profiles } = await this.supabase
          .from('profiles')
          .select('id, display_name, avatar_url')
          .in('id', Array.from(senderIds));
        
        if (profiles) {
          profiles.forEach(profile => {
            senderProfiles.set(profile.id, profile);
          });
        }
      }

      // Process results: Group message counts by channel
      const countsByChannel = new Map();
      messageCounts.forEach(({ channelId, count }) => {
        countsByChannel.set(channelId, count);
      });

      // Combine all data
      const enrichedChannels = channels.map(channel => {
        const lastMessage = latestByChannel.get(channel.id);
        if (lastMessage && lastMessage.sender_id) {
          lastMessage.sender = senderProfiles.get(lastMessage.sender_id) || {
            id: lastMessage.sender_id,
            display_name: 'Unknown',
            avatar_url: null
          };
        }
        
        return {
          ...channel,
          last_message: lastMessage || null,
          message_count: countsByChannel.get(channel.id) || 0,
          unread_count: unreadByChannel.get(channel.id) || 0,
          member_count: 0 // TODO: Implement if needed
        };
      });

      return enrichedChannels;
    });
  }

  /**
   * Get messages for a channel with efficient pagination
   */
  async getChannelMessagesOptimized(channelId: string, limit = 50, before?: string): Promise<ServiceResponse<ClubChannelMessage[]>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      let query = this.supabase
        .from('club_channel_messages')
        .select(`
          id,
          channel_id,
          sender_id,
          content,
          message_type,
          message_data,
          reply_to_id,
          thread_root_id,
          created_at,
          edited_at
        `)
        .eq('channel_id', channelId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (before) {
        query = query.lt('created_at', before);
      }

      const { data: messages, error } = await query;
      if (error) throw error;

      if (!messages || messages.length === 0) return [];

      // Get message IDs for batch reaction queries
      const messageIds = messages.map(m => m.id);

      // Batch query: Get all reactions for these messages (without join to avoid 406 errors)
      const { data: reactions } = await this.supabase
        .from('club_message_reactions')
        .select(`
          message_id,
          emoji,
          user_id,
          id
        `)
        .in('message_id', messageIds);

      // Get unique user IDs from reactions and batch fetch profiles
      const reactionUserIds = new Set<string>();
      if (reactions) {
        reactions.forEach(reaction => {
          if (reaction.user_id) {
            reactionUserIds.add(reaction.user_id);
          }
        });
      }

      // Batch query: Get reaction user profiles
      const reactionUserProfiles = new Map();
      if (reactionUserIds.size > 0) {
        const { data: profiles } = await this.supabase
          .from('profiles')
          .select('id, display_name, avatar_url')
          .in('id', Array.from(reactionUserIds));
        
        if (profiles) {
          profiles.forEach(profile => {
            reactionUserProfiles.set(profile.id, profile);
          });
        }
      }

      // Process reactions into counts and user flags
      const reactionsByMessage = new Map();
      if (reactions) {
        reactions.forEach(reaction => {
          if (!reactionsByMessage.has(reaction.message_id)) {
            reactionsByMessage.set(reaction.message_id, []);
          }
          // Add user profile to reaction
          const enrichedReaction = {
            ...reaction,
            user: reactionUserProfiles.get(reaction.user_id) || {
              id: reaction.user_id,
              display_name: 'Unknown',
              avatar_url: null
            }
          };
          reactionsByMessage.get(reaction.message_id).push(enrichedReaction);
        });
      }

      // Get all unique sender IDs
      const senderIds = new Set(messages.map(m => m.sender_id).filter(Boolean));
      
      // Batch query: Get all sender profiles
      const senderProfiles = new Map();
      if (senderIds.size > 0) {
        const { data: profiles } = await this.supabase
          .from('profiles')
          .select('id, display_name, avatar_url, handicap')
          .in('id', Array.from(senderIds));
        
        if (profiles) {
          profiles.forEach(profile => {
            senderProfiles.set(profile.id, profile);
          });
        }
      }

      // Combine messages with reactions and sender profiles
      const enrichedMessages = messages.map(message => {
        const messageReactions = reactionsByMessage.get(message.id) || [];
        const reactionCounts: Record<string, number> = {};
        const userHasReacted: Record<string, boolean> = {};

        messageReactions.forEach(reaction => {
          reactionCounts[reaction.emoji] = (reactionCounts[reaction.emoji] || 0) + 1;
          if (reaction.user_id === user.id) {
            userHasReacted[reaction.emoji] = true;
          }
        });

        const sender = message.sender_id ? 
          (senderProfiles.get(message.sender_id) || {
            id: message.sender_id,
            display_name: 'Unknown',
            avatar_url: null
          }) : null;

        return {
          ...message,
          sender,
          reactions: messageReactions,
          reaction_counts: reactionCounts,
          user_has_reacted: userHasReacted,
          mentions: [] // TODO: Implement if needed
        };
      });

      return enrichedMessages.reverse(); // Return in chronological order
    });
  }

  /**
   * Parse mentions from message content
   */
  private parseMentions(content: string): string[] {
    const mentionPattern = /@(\w+)/g;
    const mentions: string[] = [];
    let match;
    
    while ((match = mentionPattern.exec(content)) !== null) {
      mentions.push(match[1]); // Extract username without @
    }
    
    return [...new Set(mentions)]; // Remove duplicates
  }

  /**
   * Get club members for mention validation
   */
  async getClubMembers(clubId: string): Promise<ServiceResponse<UserProfile[]>> {
    return this.withErrorHandling(async () => {
      const { data: members, error } = await this.supabase
        .from('user_club_memberships')
        .select(`
          user_id,
          profiles!inner(
            id,
            nickname,
            first_name,
            last_name,
            avatar_url
          )
        `)
        .eq('club_id', clubId)
        .eq('status', 'active');

      if (error) throw error;

      return members?.map(member => ({
        id: member.profiles.id,
        display_name: member.profiles.nickname || 
                    `${member.profiles.first_name || ''} ${member.profiles.last_name || ''}`.trim() || 
                    'Unknown User',
        avatar_url: member.profiles.avatar_url,
        handicap: null // Not available in profiles table
      })) || [];
    });
  }

  /**
   * Validate and resolve mentions against club members
   */
  private async validateMentions(mentionUsernames: string[], clubId: string): Promise<string[]> {
    if (mentionUsernames.length === 0) return [];

    const membersResponse = await this.getClubMembers(clubId);
    if (!membersResponse.success) return [];

    const members = membersResponse.data;
    const validMentions: string[] = [];

    for (const username of mentionUsernames) {
      const member = members.find(m => 
        m.display_name?.toLowerCase() === username.toLowerCase()
      );
      if (member) {
        validMentions.push(member.id);
      }
    }

    return validMentions;
  }

  /**
   * Send a message with optimistic response and mention processing
   */
  async sendMessageOptimized(data: SendMessageRequest): Promise<ServiceResponse<ClubChannelMessage>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Parse mentions from content
      const mentionUsernames = data.content ? this.parseMentions(data.content) : [];
      
      // Get club ID for mention validation
      const { data: channel } = await this.supabase
        .from('club_channels')
        .select('club_id')
        .eq('id', data.channel_id)
        .single();

      if (!channel) throw new Error('Channel not found');

      // Validate mentions against club members
      const validMentionIds = await this.validateMentions(mentionUsernames, channel.club_id);

      // Insert message
      const { data: message, error } = await this.supabase
        .from('club_channel_messages')
        .insert({
          channel_id: data.channel_id,
          sender_id: user.id,
          content: data.content,
          message_type: data.message_type || 'text',
          message_data: data.message_data || {},
          reply_to_id: data.reply_to_id || null,
          thread_root_id: data.thread_root_id || null
        })
        .select(`
          id,
          channel_id,
          sender_id,
          content,
          message_type,
          message_data,
          reply_to_id,
          thread_root_id,
          created_at,
          sender:profiles(id, display_name, avatar_url, handicap)
        `)
        .single();

      if (error) throw error;

      // Insert mentions if any
      if (validMentionIds.length > 0) {
        const mentionInserts = validMentionIds.map(userId => ({
          message_id: message.id,
          mentioned_user_id: userId
        }));

        await this.supabase
          .from('club_message_mentions')
          .insert(mentionInserts);
      }

      return {
        ...message,
        sender: message.profiles, // Map the joined profile data
        reactions: [],
        reaction_counts: {},
        user_has_reacted: {},
        mentions: validMentionIds.map(userId => ({ mentioned_user_id: userId }))
      };
    });
  }

  /**
   * Subscribe to new messages in a channel
   */
  subscribeToChannelOptimized(channelId: string, callback: ChannelSubscriptionCallback) {
    const channelName = `club-channel-optimized-${channelId}`;
    
    if (this.subscriptions.has(channelName)) {
      this.unsubscribeFromChannel(channelId);
    }
    
    const channel = this.supabase
      .channel(channelName)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'club_channel_messages',
        filter: `channel_id=eq.${channelId}`
      }, async (payload) => {
        console.log('🔔 [SUBSCRIPTION DEBUG] New message received:', {
          channelId,
          eventType: payload.eventType,
          messageId: payload.new?.id,
          content: payload.new?.content,
          senderId: payload.new?.sender_id,
          replyToId: payload.new?.reply_to_id,
          threadRootId: payload.new?.thread_root_id
        });

        // Fetch the full message with profile data and mentions
        const { data: message } = await this.supabase
          .from('club_channel_messages')
          .select(`
            id,
            channel_id,
            sender_id,
            content,
            message_type,
            message_data,
            created_at,
            sender:profiles(id, display_name, avatar_url, handicap)
          `)
          .eq('id', payload.new.id)
          .single();

        if (message) {
          // Fetch mentions for this message
          const { data: mentions } = await this.supabase
            .from('club_message_mentions')
            .select(`
              mentioned_user_id,
              profiles!mentioned_user_id(id, display_name, avatar_url)
            `)
            .eq('message_id', message.id);

          callback({
            ...message,
            sender: message.profiles, // Map the joined profile data
            reactions: [],
            reaction_counts: {},
            user_has_reacted: {},
            mentions: mentions || []
          });
        }
      })
      .subscribe();
    
    this.subscriptions.set(channelName, channel);
    return channel;
  }

  /**
   * Club-wide message subscription for unread-badge updates.
   *
   * Unlike subscribeToChannelOptimized (which is scoped to the single open
   * channel), this listens for INSERTs across ALL the club's channels so that
   * channels the user is NOT currently viewing can bump their unread badge in
   * real time. RLS restricts the stream to channels the user is allowed to see.
   *
   * The callback receives a lightweight payload (channel_id, sender_id, id) —
   * enough for the hook to increment the right badge without a full message
   * fetch. The open channel already has its own subscription, so the hook
   * skips the active channel and own-sent messages.
   */
  subscribeToClubMessagesOptimized(
    clubId: string,
    channelIds: string[],
    callback: (info: { id: string; channel_id: string; sender_id: string }) => void
  ) {
    const subscriptionName = `club-messages-optimized-${clubId}`;

    if (this.subscriptions.has(subscriptionName)) {
      this.unsubscribeFromClubMessages(clubId);
    }

    // channelIds may be ['*'] (subscribe-all sentinel) or an explicit list.
    // Caller is responsible for filtering by channel; we just relay every INSERT.
    const explicitSet = channelIds && channelIds.length > 0 && channelIds[0] !== '*'
      ? new Set(channelIds)
      : null;

    const channel = this.supabase
      .channel(subscriptionName)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'club_channel_messages'
      }, (payload) => {
        const newRow = payload.new as { id?: string; channel_id?: string; sender_id?: string } | undefined;
        if (!newRow?.id || !newRow.channel_id) return;
        // If an explicit list was provided, filter here; otherwise let caller filter.
        if (explicitSet && !explicitSet.has(newRow.channel_id)) return;

        callback({
          id: newRow.id,
          channel_id: newRow.channel_id,
          sender_id: newRow.sender_id || ''
        });
      })
      .subscribe();

    this.subscriptions.set(subscriptionName, channel);
    return channel;
  }

  unsubscribeFromClubMessages(clubId: string) {
    const subscriptionName = `club-messages-optimized-${clubId}`;
    const channel = this.subscriptions.get(subscriptionName);

    if (channel) {
      channel.unsubscribe();
      this.supabase.removeChannel(channel);
      this.subscriptions.delete(subscriptionName);
    }
  }

  /**
   * Clean up subscriptions
   */
  unsubscribeFromChannel(channelId: string) {
    const channelName = `club-channel-optimized-${channelId}`;
    const channel = this.subscriptions.get(channelName);
    
    if (channel) {
      channel.unsubscribe();
      this.supabase.removeChannel(channel); // FIX: Add removeChannel
      this.subscriptions.delete(channelName);
    }
  }

  unsubscribeAll() {
    console.log('🧹 [ClubCommunityOptimizedService] Cleaning up all subscriptions');
    this.subscriptions.forEach((channel, name) => {
      try {
        channel.unsubscribe();
        this.supabase.removeChannel(channel); // FIX: Add removeChannel
      } catch (error) {
        console.error(`Error cleaning up channel ${name}:`, error);
      }
    });
    this.subscriptions.clear();
  }
  
  // FIX: Add destroy alias
  destroy() {
    this.unsubscribeAll();
  }

  /**
   * Add reaction to a message
   */
  async addReaction(messageId: string, emoji: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Use upsert — the unique constraint (message_id, user_id, emoji) prevents duplicates
      // without a separate read round-trip that generates spurious 406 errors.
      const { error } = await this.supabase
        .from('club_message_reactions')
        .upsert({
          message_id: messageId,
          user_id: user.id,
          emoji: emoji
        }, { onConflict: 'message_id,user_id,emoji' });

      if (error) throw error;
    });
  }

  /**
   * Remove reaction from a message
   */
  async removeReaction(messageId: string, emoji: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await this.supabase
        .from('club_message_reactions')
        .delete()
        .eq('message_id', messageId)
        .eq('user_id', user.id)
        .eq('emoji', emoji);

      if (error) throw error;
    });
  }

  /**
   * Subscribe to reaction updates for a channel
   */
  subscribeToReactionsOptimized(channelId: string, callback: (reaction: any) => void) {
    const channelName = `club-reactions-optimized-${channelId}`;
    
    if (this.subscriptions.has(channelName)) {
      this.unsubscribeFromReactions(channelId);
    }
    
    const channel = this.supabase
      .channel(channelName)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'club_message_reactions',
        filter: `message_id=in.(select id from club_channel_messages where channel_id = '${channelId}')`
      }, (payload) => {
        callback(payload);
      })
      .subscribe();
    
    this.subscriptions.set(channelName, channel);
    return channel;
  }

  /**
   * Unsubscribe from reaction updates
   */
  unsubscribeFromReactions(channelId: string) {
    const channelName = `club-reactions-optimized-${channelId}`;
    const channel = this.subscriptions.get(channelName);
    
    if (channel) {
      channel.unsubscribe();
      this.subscriptions.delete(channelName);
    }
  }

  /**
   * Get club channels with filters (for round sharing compatibility)
   */
  async getClubChannels(filters: {
    club_id: string;
    channel_type?: string;
    include_private?: boolean;
  }): Promise<ServiceResponse<ClubChannel[]>> {
    return this.withErrorHandling(async () => {
      let query = this.supabase
        .from('club_channels')
        .select('*')
        .eq('club_id', filters.club_id);

      if (filters.channel_type) {
        query = query.eq('channel_type', filters.channel_type);
      }

      if (!filters.include_private) {
        query = query.eq('is_private', false);
      }

      const { data: channels, error } = await query.order('position');

      if (error) throw error;
      return channels || [];
    });
  }

  /**
   * Send message (for round sharing compatibility)
   */
  async sendMessage(data: SendMessageRequest): Promise<ServiceResponse<ClubChannelMessage>> {
    // Delegate to existing optimized method
    return this.sendMessageOptimized(data);
  }

  /**
   * Ensure default channels exist (reuse from original service)
   */
  private async ensureDefaultChannels(clubId: string): Promise<void> {
    const { data: { user } } = await this.supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data: existingChannels } = await this.supabase
      .from('club_channels')
      .select('id')
      .eq('club_id', clubId)
      .limit(1);

    if (existingChannels && existingChannels.length > 0) {
      return; // Channels already exist
    }

    // Create default channels
    const defaultChannels = [
      { name: 'general', channel_type: 'text', description: 'General club discussion', position: 1 },
      { name: 'course-tips', channel_type: 'advice', description: 'Share course tips and advice', position: 2 },
      { name: 'playing-today', channel_type: 'events', description: 'Coordinate games and meetups', position: 3 },
      { name: 'round-shares', channel_type: 'round_shares', description: 'Completed round sharing', position: 4 },
      { name: 'announcements', channel_type: 'announcements', description: 'Club announcements', position: 5 }
    ] as const;

    for (const channelData of defaultChannels) {
      await this.supabase
        .from('club_channels')
        .insert({
          club_id: clubId,
          name: channelData.name,
          channel_type: channelData.channel_type,
          description: channelData.description,
          position: channelData.position,
          is_private: false,
          is_default: true,
          created_by: user.id
        });
    }
  }

  /**
   * Subscribe to channel list changes for a club (for chat interface)
   */
  subscribeToClubChannelsOptimized(clubId: string, callback: (channels: ClubChannel[]) => void) {
    const channelName = `club-channels-optimized-${clubId}`;
    
    const channel = this.supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'club_channels',
          filter: `club_id=eq.${clubId}`
        },
        async () => {
          // Reload all channels when any change occurs
          try {
            const response = await this.getClubChannelsOptimized(clubId);
            if (response.success) {
              callback(response.data || []);
            }
          } catch (error) {
            console.error('Failed to reload channels in optimized subscription:', error);
          }
        }
      )
      .subscribe();

    this.subscriptions.set(channelName, channel);
    return channel;
  }

  /**
   * Unsubscribe from club channel changes (optimized)
   */
  unsubscribeFromClubChannelsOptimized(clubId: string) {
    const channelName = `club-channels-optimized-${clubId}`;
    const channel = this.subscriptions.get(channelName);
    
    if (channel) {
      channel.unsubscribe();
      this.subscriptions.delete(channelName);
    }
  }
}

export const clubCommunityOptimizedService = ClubCommunityOptimizedService.getInstance();
export default clubCommunityOptimizedService;